import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { RotatingLines } from "react-loader-spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";

function GroupCusView() {
  const [loading, setLoading] = useState(true);
  const [stbCount, setStbCount] = useState(0);
  const [error, setError] = useState(null);
  const [groupCustomer, setGroupCustomer] = useState([]);
  const [networkName, setNetworkName] = useState([]);
  const [streetName, setStreetName] = useState([]);
  const [staffCode, setStaffCode] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const { customer_group } = useParams();

  // ✅ get cookie helper
  function getCookie(name) {
    const cookies = document.cookie.split("; ");
    for (const cookie of cookies) {
      const [key, value] = cookie.split("=");
      if (key === name) return value;
    }
    return null;
  }

  const user = getCookie("user");
  if (!user) navigate("/");

  // ✅ Fetch data
  const fetchUserData = async () => {
    try {
      console.log("sending request:", user, customer_group);

      const response = await axios.post(
        "https://node.cabletvpay.in/groupCustomerView",
        { user, customer_group }
      );

      if (response.status === 200) {
        setGroupCustomer(response.data.allCustomer || []);
        setNetworkName(response.data.net_name || []);
        setStreetName(response.data.streetDetails || []);
        setStaffCode(response.data.staffCode || []);
        setStbCount((response.data.allCustomer || []).length);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [customer_group]);

  // ✅ CSV download
  const downloadCSV = () => {
    const headers = [
      "Sno",
      "Customer__Name",
      "Acc_no",
      "Code",
      "STB_no",
      "D_no",
      "Flat_name",
      "Street__name",
      "Phone",
      "Pay Status",
      "Plan Amount",
      "Paid Amount",
      "Paid_Date",
      "Balance Amount",
      "Discount Amount",
      "Advance Amount",
      "Pending Amount",
      "Staff__Code",
      "Network",
      "Paid_time",
      "Package",
      "Added_date",
      "Collection Staff",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));
    groupCustomer.forEach((row) => {
      csvRows.push(
        [
          row.id,
          row.name,
          row.account_num,
          row.acc_num,
          row.serial_num,
          row.doorno,
          row.flat_name,
          row.street,
          row.mobile,
          row.status,
          row.pay_amt,
          row.paid_amt,
          row.paid_date,
          row.balance_amt,
          row.discount,
          row.extra_balance_amt,
          row.pend_amt,
          row.area_code,
          row.net_name,
          row.paid_time,
          row.package,
          row.adate,
          row.col_boy,
        ].join(",")
      );
    });

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "CableTvPay.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ✅ Pagination + Search
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = groupCustomer.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(groupCustomer.length / rowsPerPage);

  const filteredRows = currentRows.filter((row) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // ✅ STB copy
  const stb = useMemo(
    () => groupCustomer.map((row) => parseInt(row.serial_num, 10)),
    [groupCustomer]
  );

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(stb.join(", "))
      .then(() => {
        toast.info(`${stbCount} STB numbers copied`);
      })
      .catch((error) => {
        toast.warning("Not copied: " + error);
      });
  };

  // ✅ Loader / Error
  if (loading)
    return (
      <div className="content-wrapper">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "80vh" }}
        >
          <RotatingLines
            visible={true}
            height="80"
            width="80"
            strokeColor="#007bff"
            strokeWidth="2"
            animationDuration="0.75"
            ariaLabel="rotating-lines-loading"
          />
        </div>
      </div>
    );

  if (error)
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        Error: {error}
      </div>
    );

  // ✅ UI
  return (
    <div className="content-wrapper">
      <div className="content-header pt-1">
        <div className="my-3">
          <div className="fs-4 valuesDec h4">Group Customer Details</div>
        </div>
        <hr />

        <div
          className="card elevation-5 mt-3 mt-lg-5"
          style={{ backgroundColor: "#cccccc" }}
        >
          <div className="card-header filterBtn py-2">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between">
              <h4 className="text-navy h5">Customer Details</h4>

              {/* Search */}
              <div className="input-group w-25">
                <input
                  type="text"
                  className="form-control text-navy bg-light border-0 h-50 px-2"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderRadius: "5px 0 0 5px" }}
                />
                <span
                  className="input-group-text bg-light text-navy border-0 px-2"
                  style={{ borderRadius: "0 5px 5px 0" }}
                >
                  <i className="fas fa-search" />
                </span>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row align-items-center">
                <div className="d-flex justify-content-end btn-group">
                  <button
                    onClick={downloadCSV}
                    className="btn py-1 btn-dark border-right"
                  >
                    <i className="fas fa-file-excel" />
                    &nbsp;Excel Download
                  </button>
                  <button
                    onClick={handleCopyToClipboard}
                    className="btn py-1 btn-secondary"
                  >
                    Copy STB
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body p-0">
                      <table
  id="myTable"
  className="table table-bordered table-dark table-responsive"
>              <thead className="bg-gradient-navy">
                <tr>
                  <th>Sl_No</th>
                  <th>Customer__Name</th>
                  <th>Acc_no</th>
                  <th>Code</th>
                  <th>STB_no</th>
                  <th>D_no</th>
                  <th>Flat_name</th>
                  <th>Street__name</th>
                  <th>Phone</th>
                  <th>Pay Status</th>
                  <th>Plan Amount</th>
                  <th>Paid Amount</th>
                  <th>Paid__Date</th>
                  <th>Paid Time</th>
                  <th>Balance Amount</th>
                  <th>Discount Amount</th>
                  <th>Advance Amount</th>
                  <th>Pending_Amount</th>
                  <th>Staff__Code</th>
                  <th>Networks</th>
                  <th>Packages</th>
                  <th>Connection Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row, index) => (
                    <tr
                      key={index}
                      className="text-dark"
                      style={{
                        backgroundColor:
                          row.status === "unpaid" ? "#ffffffff" : "#fdfdfdff",
                      }}
                    >
                      <td>{row.id}</td>
                      <td>{row.name}</td>
                      <td>{row.account_num}</td>
                      <td>{row.acc_num}</td>
                      <td>{row.serial_num}</td>
                      <td>{row.doorno}</td>
                      <td>{row.flat_name}</td>
                      <td>{row.street}</td>
                      <td>{row.mobile}</td>
                      <td>{row.status}</td>
                      <td>{row.pay_amt}</td>
                      <td>{row.paid_amt}</td>
                      <td>{row.paid_date}</td>
                      <td>
                        {row.paid_time}
                      </td>
                      <td>{row.balance_amt}</td>
                      <td>{row.discount}</td>
                      <td>{row.extra_balance_amt}</td>
                      <td>{row.pend_amt}</td>
                      <td>{row.area_code}</td>
                      <td>{row.net_name}</td>
                      <td>{row.package}</td>
                      <td>
                        {row.con_status?.toLowerCase() === "active" ? (
                          <button className="btn btn-success">Active</button>
                        ) : (
                          <button className="btn btn-secondary">
                            {row.con_status}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="22" className="text-center">
                      No results found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-end mt-3">
          <div className="pagination btn-group">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              <i className="fas fa-backward"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`btn ${
                  currentPage === page
                    ? "bg-navy text-white"
                    : "btn-outline-secondary"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
            >
              <i className="fas fa-forward"></i>
            </button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default GroupCusView;
